import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { authApi } from '../api/authApi';

const AuthContext = createContext(null);

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch {
    return null;
  }
}

function isTokenExpiredOrNear(token, skewMs = 60_000) {
  const decoded = decodeJwt(token);
  if (!decoded?.exp) return true;
  return decoded.exp * 1000 <= Date.now() + skewMs;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);
  const [maskAmounts, setMaskAmounts] = useState(() => {
    const saved = localStorage.getItem('maskAmounts');
    return saved === 'true';
  });
  const [alpacaOnly, setAlpacaOnly] = useState(() => {
    const saved = localStorage.getItem('alpacaOnly');
    return saved !== 'false';
  });

  const refreshTimerRef = useRef(null);
  const refreshInFlightRef = useRef(null);
  const refreshTokenFnRef = useRef(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const clearSessionLocal = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    clearRefreshTimer();
    setAccessToken(null);
    setUser(null);
  }, [clearRefreshTimer]);

  const scheduleTokenRefresh = useCallback(
    (token) => {
      clearRefreshTimer();
      const decoded = decodeJwt(token);
      if (!decoded?.exp) return;

      const msUntilExpiry = decoded.exp * 1000 - Date.now();
      // Refresh ~2 minutes before expiry so the desk stays signed in without 2FA.
      const refreshIn = Math.max(msUntilExpiry - 2 * 60 * 1000, 5_000);

      refreshTimerRef.current = setTimeout(() => {
        refreshTokenFnRef.current?.().catch(() => {
          /* session cleared inside refresh */
        });
      }, refreshIn);
    },
    [clearRefreshTimer]
  );

  const refreshToken = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    refreshInFlightRef.current = (async () => {
      try {
        const response = await authApi.refreshToken();
        const { accessToken: token } = response.data;

        localStorage.setItem('accessToken', token);
        setAccessToken(token);
        scheduleTokenRefresh(token);
        return token;
      } catch (error) {
        clearSessionLocal();
        throw error;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();

    return refreshInFlightRef.current;
  }, [clearSessionLocal, scheduleTokenRefresh]);

  useEffect(() => {
    refreshTokenFnRef.current = refreshToken;
  }, [refreshToken]);

  // Restore session on mount; refresh quietly if access token is expired/near expiry.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const token = localStorage.getItem('accessToken');
      const userData = localStorage.getItem('user');

      if (!token || !userData) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        setUser(JSON.parse(userData));
      } catch {
        clearSessionLocal();
        if (!cancelled) setLoading(false);
        return;
      }

      if (isTokenExpiredOrNear(token)) {
        try {
          await refreshToken();
        } catch {
          /* already cleared */
        }
      } else {
        setAccessToken(token);
        scheduleTokenRefresh(token);
      }

      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
      clearRefreshTimer();
    };
    // intentionally once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password, totp = null) => {
    const response = await authApi.login(username, password, totp);
    const { accessToken: token, user: userData } = response.data;

    localStorage.setItem('accessToken', token);
    localStorage.setItem('user', JSON.stringify(userData));

    setAccessToken(token);
    setUser(userData);
    scheduleTokenRefresh(token);

    return response;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearSessionLocal();
    }
  };

  const value = {
    user,
    accessToken,
    loading,
    login,
    logout,
    refreshToken,
    isAuthenticated: !!user && !!accessToken,
    setUser,
    maskAmounts,
    setMaskAmounts: (val) => {
      const boolVal = !!val;
      setMaskAmounts(boolVal);
      localStorage.setItem('maskAmounts', boolVal ? 'true' : 'false');
    },
    alpacaOnly,
    setAlpacaOnly: (val) => {
      const boolVal = !!val;
      setAlpacaOnly(boolVal);
      localStorage.setItem('alpacaOnly', boolVal ? 'true' : 'false');
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
